import type { HttpTransport } from '@mangovoice/api-client'
import type { PaginatedApiResult, Pbx } from '@mangovoice/types'
import type { components } from '@mangovoice/types/generated/mango-api'

import { stringifyParams } from './utils'

type Company = components['schemas']['Company']

export interface PbxListParams {
  limit?: number
  offset?: number
  search?: string
}

export function createPbxesResource(transport: HttpTransport) {
  return {
    /** List PBXes via /app/v1/ prefix (user-scoped) */
    getUserPbxList: (params?: PbxListParams) => {
      const qs = stringifyParams((params ?? {}) as Record<string, unknown>)
      return transport.get<PaginatedApiResult<Pbx>>(
        qs ? `/app/v1/pbxes/?${qs}` : '/app/v1/pbxes/',
      )
    },

    getPbx: (pbxId: number | string) =>
      transport.get<Pbx>(`/app/v1/pbxes/${pbxId}/`),

    /** Get a single PBX by ID via v2 API (staff/superuser access) */
    getPbxV2: (pbxId: number) => transport.get<Pbx>(`/app/v2/pbxes/${pbxId}/`),

    /** List PBXes via internal API (unscoped / staff use) */
    getInternalPbxList: (params?: PbxListParams) => {
      const qs = stringifyParams((params ?? {}) as Record<string, unknown>)
      return transport.get<PaginatedApiResult<Pbx>>(
        qs ? `/internal/v1/pbxes/?${qs}` : '/internal/v1/pbxes/',
      )
    },

    /** Get a single PBX by ID via internal API */
    getInternalPbx: (pbxId: number) =>
      transport.get<Pbx>(`/internal/v1/pbxes/${pbxId}/`),

    /** Get PBX select options (for dropdowns) */
    select: () => transport.get<unknown>('/app/v1/pbxes-select/'),

    /** List PBXes for a company */
    listByCompany: (
      companyId: number,
      params?: { limit?: number; offset?: number },
    ) => {
      const qs = stringifyParams((params ?? {}) as Record<string, unknown>)
      return transport.get<PaginatedApiResult<Pbx>>(
        qs
          ? `/app/v1/company/${companyId}/pbxes/?${qs}`
          : `/app/v1/company/${companyId}/pbxes/`,
      )
    },

    /** List companies (internal/admin) */
    listCompanies: (params?: {
      search?: string
      limit?: number
      offset?: number
    }) => {
      const qs = stringifyParams((params ?? {}) as Record<string, unknown>)
      return transport.get<PaginatedApiResult<Company>>(
        qs ? `/internal/v1/companies/?${qs}` : '/internal/v1/companies/',
      )
    },

    /** Get company by ID (internal/admin) */
    getCompany: (companyId: number) =>
      transport.get<Company>(`/internal/v1/companies/${companyId}/`),

    /** Get time zones */
    getTimeZones: () =>
      transport.get<{ group: string; label: string; iana_time_zone: string }[]>(
        '/app/v1/time-zones/',
      ),
  }
}
